/**
 * CartSummary Component
 * 
 * Displays cart totals and checkout button.
 * 
 * @component
 * @example
 * ```tsx
 * <CartSummary cartTotal={4999} onClose={() => {}} />
 * ```
 */

import React from 'react';
import { ShoppingCart, Truck, AlertCircle } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { formatPrice } from '../../utils/formatPrice';
import { useCheckout } from '../../hooks/useCheckout';
import { useAustraliaUser } from '../../hooks/useAustraliaUser';

interface CartSummaryProps {
  cartTotal: number;
  onClose: () => void;
}

export const CartSummary: React.FC<CartSummaryProps> = ({ cartTotal, onClose }) => {
  const { cart } = useCart();
  const { handleCheckout: processCheckout, isLoading } = useCheckout();
  const { isAustralia } = useAustraliaUser();

  // Check if we need to apply shipping:
  // Free shipping applies when:
  // 1. Any item has freeShipping flag
  // 2. Any item has quantity > 1
  // 3. Cart has multiple items
  // 4. Any item has comma-separated flavors (multiple flavor selections)
  const hasMultipleItems = cart.length > 1;
  const hasMultiQuantity = cart.some(item => item.quantity > 1);
  const hasFreeShippingItem = cart.some(item => item.freeShipping === true);
  const hasMultiFlavors = cart.some(item => item.flavor && item.flavor.includes(','));
  
  const qualifiesForFreeShipping =
    isAustralia ||
    hasMultipleItems ||
    hasMultiQuantity ||
    hasFreeShippingItem ||
    hasMultiFlavors;
  
  // Shipping cost
  const shippingCost = qualifiesForFreeShipping ? 0 : 995; // $9.95 in cents
  
  // Final total with shipping
  const finalTotal = cartTotal + shippingCost;

  return (
    <div className="p-4 border-t border-white/10">      
      <div className="space-y-2 mb-4">
        <div className="flex justify-between">
          <span className="text-white/70">Subtotal</span>
          <span>${formatPrice(cartTotal)}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-1 text-white/70">
            <Truck className="w-4 h-4" /> Shipping
          </span>
          {qualifiesForFreeShipping ? (
            <span className="text-fuchsia-400 font-medium">Free</span>
          ) : (
            <span>${formatPrice(shippingCost)}</span>
          )}
        </div>
        
        {!qualifiesForFreeShipping && (
          <div className="flex items-start gap-2 px-3 py-2 bg-fuchsia-950/30 border border-fuchsia-500/20 rounded-lg mt-2">
            <AlertCircle className="w-4 h-4 text-fuchsia-400 shrink-0 mt-0.5" />
            <p className="text-sm text-white/80">
              Add one more bottle or increase quantity to qualify for <span className="text-fuchsia-400 font-medium">free shipping</span>!
            </p>
          </div>
        )}
        
        <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10">
          <span>Total</span>
          <span>${formatPrice(finalTotal)}</span>
        </div>
      </div>
      
      <button 
        onClick={processCheckout}
        disabled={isLoading || cart.length === 0}
        className="w-full bg-gradient-to-r from-[#ff00ff] via-[#ff66ff] to-[#00ffff] text-white px-6 py-3 rounded-xl font-bold hover:opacity-90 transition-all transform hover:scale-105 hover:shadow-[0_0_30px_rgba(255,0,255,0.3)] flex items-center justify-center gap-2 disabled:opacity-70"
        aria-label="Proceed to checkout"
      >
        <ShoppingCart className="w-5 h-5" />
        {isLoading ? 'Processing...' : 'Checkout'}
      </button>
      
      <button 
        onClick={onClose}
        className="w-full mt-2 bg-transparent border border-white/20 text-white px-6 py-2 rounded-xl hover:bg-white/5 transition-colors"
        aria-label="Continue shopping"
      >
        Continue Shopping
      </button>
    </div>
  );
}